import React from 'react';
import PolicyLayout from '../components/PolicyLayout';
import SEOHead from '../components/SEOHead';
import { getSEOConfig } from '../utils/seoConfig';

const AccountSupportPage: React.FC = () => {
  const seoConfig = getSEOConfig('/account-support');

  return (
    <>
      <SEOHead
        title={seoConfig.title}
        description={seoConfig.description}
        keywords={seoConfig.keywords}
        ogType={seoConfig.ogType}
      />
      <PolicyLayout title="Account & Support" lastUpdated="October 2025">
        <p>
          This page provides information about managing your GelWel account and how to get support when you need it.
        </p>

        <h2>Account Deletion Policy</h2>
        <p>
          Users can request full account and data deletion at any time. Your privacy and control over your data
          are important to us.
        </p>

        <h3>How to Delete Your Account</h3>
        <p>
          You can delete your account by:
        </p>
        <ul>
          <li>Going to Settings → Delete Account in the app, or</li>
          <li>Emailing <a href="mailto:paulfortuneltd@gmail.com">paulfortuneltd@gmail.com</a> with the subject
            "Account Deletion Request."</li>
        </ul>

        <h3>What Happens When You Delete Your Account</h3>
        <ul>
          <li>All associated personal data will be permanently deleted within 30 days of confirmation.</li>
          <li>Your profile, photos, messages, and other content will be removed from our servers.</li>
          <li>You will no longer be able to access your account or recover any data after deletion.</li>
          <li>Some data may be retained for legal or security purposes as required by law.</li>
        </ul>

        <h3>Account Suspension vs. Deletion</h3>
        <p>
          If you're not ready to permanently delete your account, you can temporarily deactivate it instead.
          Deactivated accounts can be reactivated at any time, while deleted accounts cannot be recovered.
        </p>

        <h2>Customer Service Policy</h2>
        <p>
          Our support team is dedicated to helping you with any questions, concerns, or technical issues you
          may experience while using GelWel.
        </p>

        <h3>How to Contact Support</h3>
        <p>
          You can reach our customer service team at:{' '}
          <a href="mailto:paulfortuneltd@gmail.com">paulfortuneltd@gmail.com</a>
        </p>

        <h3>Response Times</h3>
        <p>
          Our support team responds to user inquiries, feedback, and complaints within 3–5 business days.
          For urgent safety concerns, we prioritize responses within 24 hours.
        </p>

        <h3>What We Can Help With</h3>
        <ul>
          <li>Account access and login issues</li>
          <li>Technical problems with the app</li>
          <li>Questions about billing or subscriptions</li>
          <li>Privacy and security concerns</li>
          <li>Reporting inappropriate content or users</li>
          <li>General questions about GelWel features</li>
          <li>Feedback and feature requests</li>
        </ul>

        <h3>When Contacting Support</h3>
        <p>
          To help us assist you more efficiently, please include:
        </p>
        <ul>
          <li>A clear description of your issue or question</li>
          <li>Your account email address (if applicable)</li>
          <li>Screenshots or error messages (if relevant)</li>
          <li>The device and app version you're using</li>
        </ul>

        <h3>Escalation Process</h3>
        <p>
          If you're not satisfied with the initial response from our support team, you can request to escalate
          your concern to a senior support specialist. Simply reply to the support email with "Escalation Request"
          in the subject line.
        </p>

        <h2>Contact</h2>
        <p>
          For account or support inquiries:{' '}
          <a href="mailto:paulfortuneltd@gmail.com">paulfortuneltd@gmail.com</a>
        </p>
      </PolicyLayout>
    </>
  );
};

export default AccountSupportPage;
